import { Occassion, OccassionType } from './reservations';

export interface Guest {
  name: string;
  preferredLanguage: string;
  email: string;
  phone: string;
  guestId: string;
  attr: string[];
  phoneRule?: {
    connectTo: 'assistant' | 'voiceMail' | 'redirect';
    value?: string;
    type?: string;
  };
  reservations?: {
    id: string;
    date: string;
    time: string;
    guests: number;
    occassion: string;
  }[];
}

// if a restaurant has multiple occassions
// [LUNCH, DINNER] = Mittag oder Abendessen
// [BREAKFIRST, LUNCH, DINNER] = Frühstück, Mittagstisch oder Abendessen
// [FOOD, DRINKS_ONLY] = Für Essen oder nur für Getränke?

export enum ActionType {
  CONTINUE = 'continue',
  REDIRECT = 'redirect',
  SEND_SMS = 'sendSMS',
  CONTACT_PERSON = 'contactPerson',
}

export type Action = {
  action?: string;
  type?: ActionType; // ...
  payload?: any;
};

export type Prompt = {
  prompt: {
    de: string;
    en: string;
  };
  actions: {
    yes: Prompt | Action;
    no: Prompt | Action;
  };
};

export enum ShiftMode {
  INCOMING_CALL = 0,
  ERROR = 1,
  REDIRECT = 2,
  EDIT_RESA = 3,
  EVENT = 4,
}

export enum ShiftType {
  WDAYS = 0, // lowest priority
  DATES = 1, // medium priority
  TS = 2, // highest priority
}

export enum ConnectTo {
  REDIRECT = 0,
  ASSISTANT = 1,
  VOICE_MAIL = 2,
  TEXT_MESSAGE = 3,
  VOICE_MESSAGE = 4,
}

export type SiaShift = {
  id: string;
  title: string;
  mode: ShiftMode;
  type: ShiftType;
  active: boolean;
  matching: {
    start?: string; // e.g 18:00
    close?: string; // e.g 23:00
    wDays?: number[];
    dates?: string[];
    till?: number;
  };
  connectTo: ConnectTo;
  value?: any;

  //  payload: {
  //    type:PayloadType,
  //  }
};

export type GuestRule = {
  phone: string;
  name: string;
  connectTo: ConnectTo;
  value?: any;
};

export enum ContactType {
  GENERAL = 0,
  RESERVATION = 1,
  EVENT = 2,
  ERROR = 3,
}

export type Contact = {
  id: string;
  name: string;
  number: string;
  types?: ContactType[];
  unavailable?: boolean;
};

export type Contacts = Record<string, Contact>;

export enum FaqType {
  OPENING_HOURS = 0,
  CORONA = 1,
  EVENTS = 2,
  DOG = 3,
  KIDS = 4,
  JOB_OFFERS = 5,
  TAKE_AWAY = 6,
  OTHER = 7,
}

export type Answer = {
  answer: {
    de: string;
    en: string;
  };
  next?: Action;
};

export type FaqQuery = {
  id: string;
  action: Action | Prompt | Answer;
};

export type Hours = null | {
  start: string;
  close: string;
  kitchenClose?: string;
};

export type OpeningHours = {
  regular: Record<number, Hours>;
  special: {
    name: string;
    date: string;
    msg?: { de: string; en: string };
    closed: boolean;
    hours?: Hours;
  }[];
};

export type Occassions = Occassion[];

export type SiaSettings = {
  restaurantName: string;

  phoneBook: Record<string, Contact>;

  faqs: Record<number, FaqQuery>;

  openingHours?: OpeningHours;

  welcomeMessage?: {
    type: 'message' | 'audio';
    url?: { de: string; en: string };
    message?: { de: string; en: string };
  };

  fallbackMessage?: {
    type: 'message' | 'audio' | 'contactPerson';
    url?: { de: string; en: string };
    message?: { de: string; en: string };
    voiceMail?: boolean;
  };

  defaultLanguage?: 'de' | 'en' | null; // default null

  /* 
   @deprecated 
  */
  specialOccassion?: { prompt: Prompt; id: string };

  occassions: Occassions;

  lessThanMinTimeInAdvanceMessage?: Record<string, string>;

  occassionEntities?: {
    value: string;
    synonyms: string[];
  }[];

  redirectStandard: {
    value?: any;
    connectTo: ConnectTo;
  };

  standard: {
    connectTo: ConnectTo;
    value?: any;
  };

  smsWhenNoAction?: null | {
    active: boolean;
    name: string;
    sender: string;
  };

  voice_mail_on_fail?: boolean; // default is false
  voice_mail_after_fallbackMessage?: boolean; // default is false
  upcomingReservation?: boolean; // default is false

  redirectFailedCallback?: {
    active: boolean;
    message: Record<string, string>;
  };

  noAvailableTimeMessage?: Record<string, string>;

  shifts: SiaShift[];
};

export type CallEvent = {
  id: string;
  ts: number;
  actionSuggested?: boolean;
  result: string;
  success: boolean;
};

export type Conversation = {
  ts: number;
  text: string;
  response: string;
};

type MailBox = {
  recordingUrl: string;
  transcription: string;
  optimizedTranscription: string;
  duration: number;
  played?: boolean;
};
export interface SiaSession {
  sessionId: string;
  callSid: string;
  callerId: string;
  restaurantId: string;

  createdAt: number;
  endedAt: number | null;

  takenCareOf?: boolean;

  time: string;
  date: string;
  guests?: number;
  dateStr?: string;
  connectTo: 'assistant' | 'voiceMail' | 'redirect';
  value?: any;
  type: string;
  guest: Guest;
  guestRule?: GuestRule;
  canReceiveSms?: boolean;
  voicemail?: { audio: string; duration: string };
  events: CallEvent[];
  conversations: Conversation[];
  mailbox?: MailBox;

  hasMailbox?: boolean;
  language?: string;

  successfulReservation?: boolean;
  reservationId?: string;

  currentStep: string;
  previousSteps: string[];
  success?: boolean;
  recordingUrl?: string;
  rating?: number;

  callback?: {
    newCallSid: string;
    callSid: string;
    status: string;
  };
}

export interface RestaurantRules {
  restaurantId: string;
  settings: SiaSettings;
  activeSessions: Record<string, SiaSession>;
  shifts: SiaShift[];
  updatedAt: number;
}

export type DialogflowRequestBody = {
  queryParams: {
    timeZone?: string;
    geoLocation?: {
      latitude: number;
      longitude: number;
    };
    contexts?: {
      name: string;
      parameters: Record<string, String>;
      lifespanCount: number;
    }[];
    resetContexts?: boolean;
    sessionEntityTypes?: {
      name: string;
      entityOverrideMode: string; // ENTITY_OVERRIDE_MODE_UNSPECIFIED	| ENTITY_OVERRIDE_MODE_OVERRIDE | ENTITY_OVERRIDE_MODE_SUPPLEMENT
      entities: {
        value: string;
        synonyms: string[];
      }[];
    }[];
    payload?: Record<string, any>;
    sentimentAnalysisRequestConfig?: {
      analyzeQueryTextSentiment: boolean;
    };
    webhookHeaders?: Record<string, string>;
  };
  queryInput?: {
    audioConfig?: {};
    text?: {
      text: string;
      languageCode: string;
    };
    event?: {
      name: string;
      languageCode: string;
      parameters?: Record<string, string>;
    };
  };
  outputAudioConfig?: {};
  outputAudioConfigMask?: {};
  inputAudio?: {};
};

export type DialogflowResponse = {
  responseId: string;
  queryResult: {
    queryText: string;
    languageCode: string;
    speechRecognitionConfidence: number;
    action: string;
    parameters: Record<string, string>;
    allRequiredParamsPresent: boolean;
    cancelsSlotFilling: boolean;
    fulfillmentText: string;
    fulfillmentMessages: {
      platform: string;
      text: {
        text: string[];
      };
      image: {
        imageUri: string;
        accessibilityText: string;
      };
      quickReplies: {
        title: string;
        quickReplies: string[];
      };
      card: {
        title: string;
        subtitle: string;
        imageUri: string;
        buttons: {
          text: string;
          postback: string;
        }[];
      };
      payload: Record<string, any>;
      simpleResponses: {
        textToSpeech?: string;
        ssml?: string;
        displayText?: string;
      }[];
      basicCard: {
        title: string;
        subtitle: string;
        formattedText: string;
        image: {
          imageUri: string;
          accessibilityText: string;
        };
        buttons: {
          title: string;
          openUriAction: {
            uri: string;
          };
        }[];
      };
      suggestions: {
        title: string;
      }[];
      linkOutSuggestion: {
        destinationName: string;
        uri: string;
      };
      listSelect: {
        title: string;
        items: {
          title: string;
          description: string;
          info: {
            key: string;
            synonyms: string[];
          };
          image: {
            imageUri: string;
            accessibilityText: string;
          };
        }[];
      };
      carouselSelect: {
        items: {
          info: {
            key: string;
            synonyms: string[];
          };
          title: string;
          description: string;
          image: {
            imageUri: string;
            accessibilityText: string;
          };
        }[];
      };
      browseCarouselCard: {
        items: {
          title: string;
          description: string;
          image: {
            imageUri: string;
            accessibilityText: string;
          };
          footer: string;
          openUriAction: {
            uri: string;
            urlTypeHint: string; // enum: "UNSPECIFIED" | "AMP_CONTENT" | "WEB_URL" | "APP_LINK"
          };
        }[];
      };
      tableCard: {
        title: string;
        subtitle: string;
        image: {
          imageUri: string;
          accessibilityText: string;
        };
        columnProperties: {
          header: string;
          horizontalAlignment: string; // enum: "UNSPECIFIED" | "LEADING" | "CENTER" | "TRAILING"
        }[];
        rows: {
          dividerAfter: boolean;
          cells: {
            text: string;
          }[];
        }[];
      };
      mediaContent: {
        mediaType: string; // enum: "MEDIA_TYPE_UNSPECIFIED" | "AUDIO" | "VIDEO",
        mediaObjects: {
          name: string;
          description: string;
          largeImage: {
            imageUri: string;
            accessibilityText: string;
          };
          icon: {
            imageUri: string;
            accessibilityText: string;
          };
          contentUrl: string;
        }[];
      };
    }[];
    webhookSource: string;
    webhookPayload: Record<string, any>;
    outputContexts: {
      name: string;
      lifespanCount: number;
      parameters: Record<string, string>;
    }[];
    intent: {
      name: string;
      displayName: string;
      webhookState: string; // enum: "WEBHOOK_STATE_UNSPECIFIED" | "WEBHOOK_STATE_ENABLED" | "WEBHOOK_STATE_ENABLED_FOR_SLOT_FILLING"
      priority: number;
      isFallback: boolean;
      mlDisabled: boolean;
      liveAgentHandoff: boolean;
      inputContextNames: string[];
      events: string[];
      trainingPhrases: {
        type: string; // enum: "EXAMPLE" | "TEMPLATE"
        parts: {
          text: string;
          entityType?: string;
          alias?: string;
          userDefined?: boolean;
        }[];
        timesAddedCount?: number;
      }[];
      outputContexts: {
        name: string;
        lifespanCount: number;
        parameters: Record<string, string>;
      }[];
      inputContexts: {
        name: string;
        lifespanCount: number;
        parameters: Record<string, string>;
      }[];
      resetContexts: boolean;
      parameters: any[];
      messages: any[];
      defaultResponsePlatforms: string[];
      rootFollowupIntentName: string;
      parentFollowupIntentName: string;
      followupIntentInfo: {
        followupIntentName: string;
        parentFollowupIntentName: string;
        triggerPhrases: string[];
        parameterNames: string[];
      }[];
    };
    intentDetectionConfidence: number;
    diagnosticInfo: {
      end_conversation?: boolean;
      [key: string]: any;
    };
    sentimentAnalysisResult: {
      queryTextSentiment: {
        magnitude: number;
        score: number;
      };
      languageCode: string;
    };
  };
  webhookStatus: {
    code: number;
    message: string;
    details: {
      '@type': string;
      [key: string]: any;
    }[];
  };
  outputAudio: string;
  outputAudioConfig: {
    audioEncoding: string; // enum: "OUTPUT_AUDIO_ENCODING_UNSPECIFIED" | "OUTPUT_AUDIO_ENCODING_LINEAR_16" | "OUTPUT_AUDIO_ENCODING_MP3" | "OUTPUT_AUDIO_ENCODING_OGG_OPUS" | "OUTPUT_AUDIO_ENCODING_AMR_NB" | "OUTPUT_AUDIO_ENCODING_AMR_WB" | "OUTPUT_AUDIO_ENCODING_MPEG_4_AAC" | "OUTPUT_AUDIO_ENCODING_SPEEX_WITH_HEADER_BYTE" | "OUTPUT_AUDIO_ENCODING_RAW_OPUS"
    sampleRateHertz: number;
    synthesizeSpeechConfig: {
      speakingRate: number;
      pitch: number;
      volumeGainDb: number;
      effectsProfileId: string[];
      voice: {
        name: string;
        ssmlGender: string;
      };
    };
  };
};

export interface CallCounterDocument {
  monthId: string;
  restaurantId: string;
  calls: {
    id: string;
    d: number; // duration
    t: 'r' | 'a' | 'e' | 'm'; // redirect | assistant | error | message
    r: boolean; // reservation
  }[];
}

export type MailBoxEntry = {
  id: string; // same as callId
  recordingUrl: string;
  transkript: {
    original?: string;
    optimized?: string;
  };

  restaurantId: string;
  callerId: string;
  callerName?: string;
};

export type Stats = {
  numberOfCalls: number;
  numberOfWarnings: number;
  numberOfErrors: number;
  numberOfReservations: number;
  averageCallLengthInSec: number;
  numberOfSuccessfulRedirects: number;
  numberOfFailedRedirects: number;
  numberOfNoSeats: number;
  numberOfNoAction: number;
  numberOfMessagesPlayed: number;
  timeSavedInMinutes: number;
  numberOfMinutes: number;
  numberOfReservationGuests: number;
  billableMinutes: number;
  projectedCosts: number;
  successfulCalls: number;
  projectedCostsRedirectOnly: number;
  numberOfCallsThatCalledAgain: number;
  mailbox: number;
  percentage: {
    noAction: number;
    pressedTwo: number;
    reservations: number;
    noSeats: number;
    hangUps: number;
    mailbox: number;
  };
};

export type CallsPerHour = {
  title: string;
  hour: string;
  stats: Stats;
  count?: number;
  reservations?: number;
  messages?: number;
  failedRedirects?: number;
  successfulRedirects?: number;
}[];

export type CallStatistics = {
  stats: Stats;
  callsPerHour: CallsPerHour;
  restaurantId: string;
  week: string;
};
